import { createContext, useContext, useCallback, type ReactNode } from 'react'
import { useAppSelector, useAppDispatch } from '../store/hooks'
import { updateTaskSaga, deleteTaskSaga } from '../store/tasks/tasksSlice'

export interface Task {
    id: string
    workspaceId: string
    projectId: string
    listId: string
    number: number
    name: string
    description: unknown
    ownerId: string | null
    apiKeyName?: string | null
    assigneeId: string | null
    statusId: string
    labels: string[]
    order: number
    isCompleted: boolean
    createdAt: any
    updatedAt: any
}

interface TaskContextValue {
    task: Task | null
    loading: boolean
    error: string | null
    updateTask: (data: Partial<Pick<Task, 'name' | 'description' | 'assigneeId' | 'statusId' | 'labels' | 'order' | 'isCompleted'>>) => Promise<void>
    deleteTask: () => Promise<void>
}

const TaskContext = createContext<TaskContextValue | null>(null)

export const useTask = () => {
    const context = useContext(TaskContext)
    if (!context) {
        throw new Error('useTask must be used within TaskProvider')
    }
    return context
}

// Returns context if component is inside TaskProvider, otherwise null.
// For places where task may or may not be present (e.g. TextEditor).
export const useOptionalTask = (): TaskContextValue | null => useContext(TaskContext)

interface TaskProviderProps {
    children: ReactNode
    taskId: string
}

export const TaskProvider = ({ children, taskId }: TaskProviderProps) => {
    const dispatch = useAppDispatch()

    const task = useAppSelector(state => state.tasks.byId[taskId] ?? null)
    const loading = useAppSelector(state => state.tasks.loading)
    const error = useAppSelector(state => state.tasks.error)

    const updateTask = useCallback(async (data: Partial<Pick<Task, 'name' | 'description' | 'assigneeId' | 'statusId' | 'labels' | 'order' | 'isCompleted'>>): Promise<void> => {
        if (!taskId || !task) return
        dispatch(updateTaskSaga({ taskId, data }))
    }, [dispatch, taskId, task])

    const deleteTask = useCallback(async (): Promise<void> => {
        if (!taskId) return
        dispatch(deleteTaskSaga(taskId))
    }, [dispatch, taskId])

    const value: TaskContextValue = {
        task,
        loading,
        error,
        updateTask,
        deleteTask
    }

    return (
        <TaskContext.Provider value={value}>
            {children}
        </TaskContext.Provider>
    )
}
